import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { finalize } from 'rxjs';
import { resolveApiErrorMessage } from 'src/app/core/http/api-http-error';
import {
  ProductApiService,
  ProductDocument,
  ProductDocumentUpdateItem,
} from 'src/app/core/services/product-api.service';
import {
  DEFAULT_LIST_MODULE_FILTERS,
  ListCellDefDirective,
  ListModuleColumnDef,
  ListModuleFilters,
  ListModulePaginationMeta,
  ListModuleSurfaceComponent,
  ListModuleSurfaceConfig,
  ListModuleTableComponent,
} from 'src/app/shared/components/list-module';
import { IconModule } from 'src/app/shared/icon/icon.module';
import { PageSurfaceHeaderComponent } from 'src/app/shared/page-surface-header/page-surface-header.component';
import {
  filterRecordsBySearch,
  filterRecordsByStatus,
  paginateClientRecords,
} from 'src/app/shared/utils/client-list.util';

type ProductDocumentRow = ProductDocument & Record<string, unknown>;

@Component({
  selector: 'app-product-documents',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    IconModule,
    PageSurfaceHeaderComponent,
    ListModuleSurfaceComponent,
    ListModuleTableComponent,
    ListCellDefDirective,
  ],
  templateUrl: './product-documents.component.html',
})
export class ProductDocumentsComponent implements OnInit {
  /** Liveness anchor — proves the customer is present, so it can never be switched off here. */
  readonly LOCKED_CODE = 'SELFIE';

  private readonly route = inject(ActivatedRoute);
  private readonly api = inject(ProductApiService);

  readonly slug = this.route.snapshot.paramMap.get('slug') ?? '';

  /**
   * Required/Order are per-product now, but the tenant databases only read them once the
   * document registry has been synced with the new column. Until then an operator's change is
   * live for admin and simulation and not yet for customer journeys — say so rather than letting
   * them guess.
   */
  readonly rolloutNotice =
    'Per-product settings apply to the admin and simulation now; customer journeys pick them up after the document registry sync.';

  readonly listSurfaceConfig: ListModuleSurfaceConfig = {
    toolbar: {
      search: { placeholder: 'Search document, code…' },
      status: {
        options: [
          { value: '', label: 'All documents' },
          { value: 'Active', label: 'Collected' },
          { value: 'Inactive', label: 'Not collected' },
        ],
      },
      dateRange: false,
    },
    pagination: true,
  };

  readonly columns: ListModuleColumnDef[] = [
    { id: 'selected', header: 'Collected' },
    { id: 'document', header: 'Document' },
    { id: 'required', header: 'Required' },
    { id: 'order', header: 'Order' },
    { id: 'capture_modes', header: 'Capture modes' },
  ];

  readonly rows = signal<ProductDocument[]>([]);
  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly error = signal('');
  readonly notice = signal('');

  listFilters: ListModuleFilters = { ...DEFAULT_LIST_MODULE_FILTERS };
  currentPage = 1;
  readonly perPage = 10;

  readonly payload = computed(() => ({
    items: this.rows().map(
      (r): ProductDocumentUpdateItem => ({
        code: r.code,
        selected: r.selected,
        required: r.required,
        order: r.order,
      }),
    ),
  }));

  ngOnInit(): void {
    this.load();
  }

  get filteredRecords(): ProductDocumentRow[] {
    let next = filterRecordsBySearch(this.searchRows, this.listFilters.searchQuery, [
      'display_name',
      'code',
      'hint',
      'capture_modes',
    ]);
    next = filterRecordsByStatus(next, this.listFilters.status, 'selected');
    return next;
  }

  get pagedRecords(): ProductDocumentRow[] {
    return paginateClientRecords(this.filteredRecords, this.currentPage, this.perPage).pageRows;
  }

  get paginationMeta(): ListModulePaginationMeta {
    return paginateClientRecords(this.filteredRecords, this.currentPage, this.perPage).meta;
  }

  get emptyMessage(): string {
    if (this.rows().length) return 'No documents match your filters.';
    return 'No documents in the catalogue.';
  }

  onListFiltersChange(patch: Partial<ListModuleFilters>): void {
    this.listFilters = { ...this.listFilters, ...patch };
    this.currentPage = 1;
  }

  onListPageChange(page: number): void {
    this.currentPage = page;
  }

  load(): void {
    this.loading.set(true);
    this.error.set('');
    this.api
      .listDocumentCatalogue(this.slug)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (res) => {
          this.rows.set(res.data ?? []);
          this.currentPage = 1;
        },
        error: (err) => this.error.set(resolveApiErrorMessage(err)),
      });
  }

  isLocked(code: string): boolean {
    return code === this.LOCKED_CODE;
  }

  toggleSelected(code: string): void {
    if (this.isLocked(code)) return;
    this.rows.update((list) =>
      list.map((r) => (r.code === code ? { ...r, selected: !r.selected } : r)),
    );
  }

  /** Required is editable only for a document this product actually collects. */
  canEditRequired(row: { code: string; selected: boolean }): boolean {
    return row.selected && !this.isLocked(row.code);
  }

  toggleRequired(code: string): void {
    if (this.isLocked(code)) return;
    // A document the product does not collect cannot be "required" — the API drops the override
    // on deselect anyway, so letting it be toggled here only invents a setting with no effect.
    if (!this.rows().find((r) => r.code === code)?.selected) return;
    this.rows.update((list) =>
      list.map((r) => (r.code === code ? { ...r, required: !r.required } : r)),
    );
  }

  setOrder(code: string, order: number): void {
    if (this.isLocked(code)) return;
    this.rows.update((list) => list.map((r) => (r.code === code ? { ...r, order } : r)));
  }

  save(): void {
    this.saving.set(true);
    this.error.set('');
    this.notice.set('');
    this.api
      .updateDocuments(this.slug, this.payload().items)
      .pipe(finalize(() => this.saving.set(false)))
      .subscribe({
        next: () => {
          this.notice.set('Saved');
          this.load();
        },
        error: (err) => this.error.set(resolveApiErrorMessage(err)),
      });
  }

  private get searchRows(): ProductDocumentRow[] {
    return this.rows() as ProductDocumentRow[];
  }
}
